import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  Cpu,
  ShieldCheck,
  WifiOff,
  GitBranch,
  Network,
  Clock,
  Share2,
  Fingerprint,
  Check,
  Building2,
  User,
  FileCheck2,
  Eye,
  ScrollText,
  Lock,
  Layers,
  Terminal,
  Gauge,
} from "lucide-react";
import SiteHeader from "../components/SiteHeader";
import Backers from "../components/Backers";
import PyPiBadge from "../components/PyPiBadge";
import CodeCopy from "../components/CodeCopy";
import CodeTabs from "../components/CodeTabs";
import CompareMatrix from "../components/CompareMatrix";
import Roadmap from "../components/Roadmap";
import SovereignCounter from "../components/SovereignCounter";
import Footer from "../components/Footer";
import Reveal from "../components/Reveal";
import VaultMark from "../components/VaultMark";
import ElevateBadge from "../components/ElevateBadge";
import WaitlistForm from "../components/WaitlistForm";
import { useWaitlist } from "../context/WaitlistContext";
import { api } from "../lib/api";

const DATA_IMG =
  "https://images.unsplash.com/photo-1518980120692-3cfe64c152d0?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzB8MHwxfHNlYXJjaHwyfHxzZWN1cmUlMjBzZXJ2ZXIlMjBkYXRhJTIwc292ZXJlaWdudHklMjBhYnN0cmFjdHxlbnwwfHx8Ymx1ZXwxNzg1NDc0MTI3fDA&ixlib=rb-4.1.0&q=85";

const stats = [
  { v: "100%", l: "chunk hashes reproducible on re-ingest" },
  { v: "0", l: "cloud dependencies required to answer" },
  { v: "3", l: "fused retrieval signals per query" },
  { v: "∞", l: "your data, yours, forever" },
];

// comparison data now lives in components/CompareMatrix.jsx

const benefits = {
  enterprise: {
    title: "The Smritium Membrane",
    tagline: "The compliance layer your regulators have been asking for.",
    body: "When something truly must cross to a cloud model, the Membrane detects and redacts PII, shows exactly what is about to leave, requires human approval, and writes a tamper-evident egress audit log. Legal, finance, healthcare and government teams get provable data sovereignty — not a policy PDF.",
    points: [
      { icon: Eye, t: "PII detection & redaction", d: "See exactly what would leave before it does." },
      { icon: ScrollText, t: "Tamper-evident egress audit", d: "Every byte that ever left, provable." },
      { icon: Lock, t: "On-prem / air-gapped", d: "Deploy org-wide, entirely inside your walls." },
      { icon: FileCheck2, t: "Compliance posture", d: "DPDP & GDPR by architecture, not paperwork." },
    ],
  },
  individual: {
    title: "Your memory, your machine",
    tagline: "Give every assistant deep context — without handing over your life.",
    body: "Ingest your notes, mail and documents, then ask questions and get cited answers — even offline. Your memory is portable by design, so you can fire any model without losing yourself. It is unified personal intelligence that finally belongs to you.",
    points: [
      { icon: Fingerprint, t: "Total data ownership", d: "Capture, embed and reason entirely on-device." },
      { icon: Layers, t: "Unified recall across tools", d: "One substrate feeds every model you use." },
      { icon: ShieldCheck, t: "Cited answers you can trust", d: "Every claim traces back to a source chunk." },
      { icon: Share2, t: "Zero cloud lock-in", d: "Export and re-land your memory anywhere." },
    ],
  },
};

function SectionHead({ over, title, sub, center }) {
  return (
    <div className={center ? "mx-auto max-w-3xl text-center" : "max-w-3xl"}>
      <p className="overline">{over}</p>
      <h2 className="mt-4 font-display text-3xl font-light leading-tight tracking-tight text-paper md:text-[2.6rem]">
        {title}
      </h2>
      {sub && <p className="mt-5 text-lg font-light leading-relaxed text-mute">{sub}</p>}
    </div>
  );
}

export default function Landing() {
  const { openWaitlist } = useWaitlist();
  const [tab, setTab] = useState("enterprise");
  const [count, setCount] = useState(null);

  useEffect(() => {
    api
      .get("/waitlist/count")
      .then((r) => setCount(r.data.total))
      .catch(() => {});
  }, []);

  const active = benefits[tab];

  return (
    <div className="relative overflow-hidden bg-ink">
      <SiteHeader />

      {/* HERO */}
      <section className="relative overflow-hidden pt-16 pb-24 md:pt-24">
        <div className="absolute inset-0 bg-grid opacity-60" />
        <div className="absolute left-1/2 top-24 h-[520px] w-[520px] -translate-x-1/2 bg-radial-ember blur-3xl" />
        <div className="container-x relative">
          <div className="grid items-center gap-14 lg:grid-cols-[1.15fr_0.85fr]">
            <div>
              <Reveal>
                <div className="mb-6 inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-4 py-1.5">
                  <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-ember" />
                  <span className="font-mono text-[11px] uppercase tracking-overline text-mute">
                    Coming soon · Private beta 2026
                  </span>
                </div>
              </Reveal>
              <Reveal delay={0.05}>
                <h1 className="font-display text-5xl font-light leading-[1.05] tracking-tight text-paper md:text-7xl">
                  The sovereign
                  <br />
                  memory layer <span className="italic text-ember">for AI.</span>
                </h1>
              </Reveal>
              <Reveal delay={0.12}>
                <p className="mt-7 max-w-xl text-lg font-light leading-relaxed text-mute">
                  Smritium is a local-first memory substrate that gives every AI
                  model deep, private context — for enterprises and individuals —
                  without surrendering a single byte to the cloud.
                </p>
              </Reveal>
              <Reveal delay={0.18}>
                <p className="mt-3 font-mono text-sm text-mute/80">
                  Unified Enterprise &amp; Personal Intelligence Infrastructure
                  (UPII).
                </p>
              </Reveal>
              <Reveal delay={0.24}>
                <div className="mt-9 flex flex-wrap items-center gap-4">
                  <button
                    onClick={() => openWaitlist()}
                    className="btn-primary"
                    data-testid="hero-waitlist-button"
                  >
                    Join the Waitlist <ArrowRight size={16} />
                  </button>
                  <Link to="/about" className="btn-ghost" data-testid="hero-about-button">
                    Read the research
                  </Link>
                </div>
              </Reveal>
              <Reveal delay={0.3}>
                <div className="mt-8 flex flex-wrap items-center gap-3">
                  <ElevateBadge />
                  <PyPiBadge />
                </div>
              </Reveal>
            </div>

            <Reveal delay={0.2} className="flex justify-center lg:justify-end">
              <VaultMark size={300} />
            </Reveal>
          </div>

          {/* stat row */}
          <Reveal delay={0.15}>
            <div className="mt-20 grid grid-cols-2 gap-px overflow-hidden rounded-2xl border border-white/10 bg-white/5 md:grid-cols-4">
              {stats.map((s) => (
                <div key={s.l} className="bg-void/60 px-6 py-7">
                  <p className="font-display text-4xl text-ember">{s.v}</p>
                  <p className="mt-2 text-xs leading-relaxed text-mute">{s.l}</p>
                </div>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      <Backers />

      {/* WHAT IS SMRITIUM */}
      <section className="border-t border-white/5 py-24 md:py-28">
        <div className="container-x">
          <Reveal>
            <SectionHead
              over="What is Smritium"
              title="Cloud memory products rent your own mind back to you. Smritium inverts the architecture."
              sub="Most memory and “second-brain” systems upload your corpus to a provider, embed it remotely, and meter your recall by the token. Smritium keeps capture, embedding, retrieval and reasoning on your machine — and treats any cloud model as an optional, swappable accelerator, never a dependency. It is the memory substrate that sits beneath every model and agent you use, so your context outlives any single app and stays under your control."
              center
            />
          </Reveal>
          <Reveal delay={0.1}>
            <div className="mx-auto mt-12 max-w-2xl overflow-hidden rounded-xl border border-white/10 bg-black/40 font-mono text-sm">
              <div className="flex items-center gap-2 border-b border-white/10 px-4 py-2.5">
                <span className="h-2.5 w-2.5 rounded-full bg-white/20" />
                <span className="h-2.5 w-2.5 rounded-full bg-white/20" />
                <span className="h-2.5 w-2.5 rounded-full bg-ember/60" />
                <span className="ml-2 text-[11px] text-mute">smritium — on your device</span>
              </div>
              <div className="space-y-2 px-5 py-5 leading-relaxed">
                <p className="text-mute">
                  <span className="text-ember">$</span> smritium ask "what did we agree on latency?"
                </p>
                <p className="text-paper">
                  Answer: A 6-hour delivery SLA, relaxed to 12 hours during the pilot.
                </p>
                <p className="text-mute/80">
                  Sources: [1] meetings/sync.md (chunk a3f2c9) · [2] terms.md (chunk 77d01b)
                </p>
                <p className="text-mute/60">✓ answered offline · 0 bytes left the device</p>
              </div>
            </div>
          </Reveal>
          <Reveal delay={0.15}>
            <div className="mx-auto mt-6 max-w-2xl">
              <p className="mb-3 text-center text-sm text-mute">
                Try the open-source core today — the engine is live on PyPI:
              </p>
              <CodeCopy command="pip install upii" />
            </div>
          </Reveal>
        </div>
      </section>

      {/* SUBSTRATE / FEATURES BENTO */}
      <section id="substrate" className="scroll-mt-32 border-t border-white/5 py-24 md:py-28">
        <div className="container-x">
          <Reveal>
            <SectionHead
              over="A local memory substrate for AI models"
              title="Everything a memory layer should do. Nothing it should ever see."
            />
          </Reveal>

          <div className="mt-14 grid gap-5 md:grid-cols-12">
            <Reveal className="md:col-span-8">
              <div className="card card-hover h-full">
                <Cpu className="text-ember" size={26} />
                <h3 className="mt-5 font-display text-2xl text-paper">
                  Sovereign by construction
                </h3>
                <p className="mt-3 max-w-xl leading-relaxed text-mute">
                  Embeddings are computed locally, vectors live in an on-disk
                  store, and metadata stays in local storage. No byte of your
                  corpus is required to leave the device for the system to work —
                  not as a setting, as an architecture.
                </p>
              </div>
            </Reveal>
            <Reveal delay={0.05} className="md:col-span-4">
              <div className="card card-hover h-full">
                <ShieldCheck className="text-ember" size={26} />
                <h3 className="mt-5 font-display text-2xl text-paper">
                  Verifiable memory
                </h3>
                <p className="mt-3 leading-relaxed text-mute">
                  Every answer traces to its source chunks via content-addressed
                  IDs. When confidence is low, it abstains instead of inventing a
                  citation.
                </p>
              </div>
            </Reveal>
            <Reveal delay={0.05} className="md:col-span-6">
              <div className="card card-hover h-full">
                <Network className="text-ember" size={26} />
                <h3 className="mt-5 font-display text-2xl text-paper">
                  The Context Rehydrator
                </h3>
                <p className="mt-3 leading-relaxed text-mute">
                  Recall is a sensor-fusion problem, not a cosine distance.
                  Smritium fuses three orthogonal signals into one ranked context
                  window:
                </p>
                <div className="mt-5 flex flex-wrap gap-3">
                  {[
                    { i: Layers, t: "Semantic" },
                    { i: Clock, t: "Temporal" },
                    { i: Share2, t: "Relational" },
                  ].map((x) => (
                    <span
                      key={x.t}
                      className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-3.5 py-1.5 text-xs text-paper"
                    >
                      <x.i size={13} className="text-ember" /> {x.t}
                    </span>
                  ))}
                </div>
              </div>
            </Reveal>
            <Reveal delay={0.1} className="md:col-span-6">
              <div className="card card-hover h-full">
                <WifiOff className="text-ember" size={26} />
                <h3 className="mt-5 font-display text-2xl text-paper">
                  Graceful degradation
                </h3>
                <p className="mt-3 leading-relaxed text-mute">
                  No GPU, no API key, no internet? Retrieval still answers, and a
                  local model keeps reasoning working. Offline is a first-class
                  mode, never a failure state.
                </p>
              </div>
            </Reveal>

            {[
              {
                i: GitBranch,
                t: "Content-addressed ingestion",
                d: "Documents are hashed on content, not path. Same inputs always yield the same memory state — idempotent and reproducible.",
              },
              {
                i: ShieldCheck,
                t: "Consent-gated capture",
                d: "Ambient capture routes through an approval inbox before anything enters durable memory. Nothing is remembered without consent.",
              },
              {
                i: Fingerprint,
                t: "Portable by design",
                d: "Lift your substrate off one host and re-land it on another without losing continuity. User-owned is enforceable by exit.",
              },
            ].map((x, idx) => (
              <Reveal key={x.t} delay={idx * 0.05} className="md:col-span-4">
                <div className="card card-hover h-full">
                  <x.i className="text-ember" size={22} />
                  <h3 className="mt-4 font-display text-xl text-paper">{x.t}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-mute">{x.d}</p>
                </div>
              </Reveal>
            ))}
          </div>

          <Reveal delay={0.1}>
            <div className="mt-12 flex justify-center">
              <button onClick={() => openWaitlist()} className="btn-ghost" data-testid="substrate-cta">
                Get early access to the substrate <ArrowRight size={16} />
              </button>
            </div>
          </Reveal>
        </div>
      </section>

      {/* BUILT FOR DEVELOPERS */}
      <section id="developers" className="scroll-mt-32 border-t border-white/5 py-24 md:py-28">
        <div className="container-x">
          <div className="grid gap-12 lg:grid-cols-[0.92fr_1.08fr] lg:items-center">
            <Reveal>
              <p className="overline">Built for developers</p>
              <h2 className="mt-4 font-display text-3xl font-light leading-tight tracking-tight text-paper md:text-[2.6rem]">
                A memory layer you can read, run, and verify.
              </h2>
              <p className="mt-5 max-w-md leading-relaxed text-mute">
                No black-box cloud. Install the engine, point it at a folder, and
                query it from your terminal, your code, or an MCP-connected agent.
                Deterministic ingestion means the same inputs always produce the
                same memory — so citations are stable, testable, and reproducible
                in CI.
              </p>
              <div className="mt-8 grid max-w-md grid-cols-2 gap-3">
                {[
                  { n: "01", t: "Ingest", d: "content-addressed" },
                  { n: "02", t: "Chunk", d: "deterministic" },
                  { n: "03", t: "Rehydrate", d: "3 signals fused" },
                  { n: "04", t: "Answer", d: "cited · offline" },
                ].map((s) => (
                  <div
                    key={s.n}
                    className="rounded-xl border border-white/10 bg-void/60 p-4"
                  >
                    <p className="font-mono text-xs text-ember">{s.n}</p>
                    <p className="mt-1 font-display text-lg text-paper">{s.t}</p>
                    <p className="text-xs text-mute">{s.d}</p>
                  </div>
                ))}
              </div>
              <div className="mt-8">
                <button
                  onClick={() => openWaitlist()}
                  className="btn-ghost"
                  data-testid="developers-cta"
                >
                  <Terminal size={16} /> Get developer preview access
                </button>
              </div>
            </Reveal>
            <Reveal delay={0.1}>
              <CodeTabs />
            </Reveal>
          </div>
        </div>
      </section>

      {/* UPII */}
      <section className="border-t border-white/5 bg-indigo/10 py-24 md:py-28">
        <div className="container-x">
          <div className="grid gap-14 lg:grid-cols-[0.9fr_1.1fr]">
            <Reveal>
              <SectionHead
                over="Unified Enterprise & Personal Intelligence Infrastructure"
                title="Personal context is a primitive, not a feature."
              />
            </Reveal>
            <Reveal delay={0.1}>
              <div className="space-y-6 text-lg font-light leading-relaxed text-mute">
                <p>
                  Today's AI stack has models, agents and connectors — but no
                  memory-and-identity substrate that outlives any single app and
                  that a person or an organisation actually controls. UPII is that
                  missing layer: a single, persistent, owner-controlled context
                  graph that sits beneath every model, agent and application.
                </p>
                <p>
                  Whoever holds the richest, freshest representation of a person —
                  or an enterprise — produces the best output. UPII makes the
                  natural owner of that representation the person and the
                  organisation, not the platform. Fire any model without losing
                  yourself.
                </p>
                <div className="flex flex-wrap gap-3 pt-2">
                  {[
                    "Identity you hold",
                    "Retrieval any agent can bind to",
                    "Capability grants you can revoke",
                    "A tamper-evident audit trail",
                  ].map((t) => (
                    <span
                      key={t}
                      className="inline-flex items-center gap-2 rounded-full border border-ember/25 bg-ember/5 px-3.5 py-1.5 text-sm text-paper"
                    >
                      <Check size={14} className="text-ember" /> {t}
                    </span>
                  ))}
                </div>
                <button
                  onClick={() => openWaitlist("enterprise")}
                  className="btn-primary mt-2"
                  data-testid="upii-cta"
                >
                  Build on UPII <ArrowRight size={16} />
                </button>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* DIFFERENCE / COMPARISON */}
      <section id="difference" className="scroll-mt-32 border-t border-white/5 py-24 md:py-28">
        <div className="container-x">
          <Reveal>
            <SectionHead
              over="How Smritium compares"
              title="Most “memory for AI” is a cloud you rent. Smritium is a substrate you own."
              sub="The mainstream approach ships memory as a metered, cloud-hosted API. Smritium ships it as a local substrate — sovereign, verifiable, and offline by default. Here's how the architectures diverge."
              center
            />
          </Reveal>

          <Reveal delay={0.1}>
            <div className="mx-auto mt-14 max-w-5xl">
              <CompareMatrix />
              <p className="mt-5 text-center text-xs text-mute">
                <span className="text-ember">✓</span> supported &nbsp;·&nbsp; —
                partial &nbsp;·&nbsp; <span className="text-mute/50">✕</span> not
                supported. Reflects typical cloud memory APIs and local RAG
                toolkits.
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      <SovereignCounter />

      {/* BENEFITS TABS */}
      <section className="border-t border-white/5 py-24 md:py-28">
        <div className="container-x">
          <Reveal>
            <SectionHead
              over="Who it's for"
              title="One substrate. Two kinds of sovereignty."
              center
            />
          </Reveal>

          <Reveal delay={0.08}>
            <div className="mx-auto mt-10 flex w-fit items-center gap-1 rounded-full border border-white/10 bg-white/5 p-1">
              <button
                onClick={() => setTab("enterprise")}
                className={`flex items-center gap-2 rounded-full px-6 py-2.5 text-sm transition-colors ${
                  tab === "enterprise" ? "bg-ember text-paper" : "text-mute hover:text-paper"
                }`}
                data-testid="benefits-tab-enterprise"
              >
                <Building2 size={15} /> For Enterprises
              </button>
              <button
                onClick={() => setTab("individual")}
                className={`flex items-center gap-2 rounded-full px-6 py-2.5 text-sm transition-colors ${
                  tab === "individual" ? "bg-ember text-paper" : "text-mute hover:text-paper"
                }`}
                data-testid="benefits-tab-individual"
              >
                <User size={15} /> For Individuals
              </button>
            </div>
          </Reveal>

          <Reveal delay={0.12}>
            <div className="mt-12 grid gap-10 lg:grid-cols-[0.9fr_1.1fr]" data-testid="benefits-panel">
              <div>
                <p className="overline">{active.title}</p>
                <h3 className="mt-4 font-display text-3xl font-light leading-tight text-paper">
                  {active.tagline}
                </h3>
                <p className="mt-5 leading-relaxed text-mute">{active.body}</p>
                <button
                  onClick={() => openWaitlist(tab)}
                  className="btn-primary mt-8"
                  data-testid="benefits-cta"
                >
                  {tab === "enterprise"
                    ? "Become a design partner"
                    : "Join as an early user"}
                  <ArrowRight size={16} />
                </button>
              </div>
              <div className="grid gap-4 sm:grid-cols-2">
                {active.points.map((p) => (
                  <div key={p.t} className="card card-hover">
                    <p.icon className="text-ember" size={22} />
                    <h4 className="mt-4 font-display text-lg text-paper">{p.t}</h4>
                    <p className="mt-2 text-sm leading-relaxed text-mute">{p.d}</p>
                  </div>
                ))}
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* BENCHMARKS / VERIFIABILITY */}
      <section className="border-t border-white/5 py-24 md:py-28">
        <div className="container-x">
          <Reveal>
            <SectionHead
              over="Measured, not asserted"
              title="Verifiability is our benchmark."
              sub="We publish the harness, not just the headline. Re-ingest the same corpus and reproduce 100% of chunk hashes — a citation you got last month still points at exactly the same evidence today."
              center
            />
          </Reveal>
          <div className="mx-auto mt-14 grid max-w-4xl gap-5 sm:grid-cols-2 md:grid-cols-4">
            {[
              { v: "0.958", l: "Recall@10 on a committed labelled set" },
              { v: "100%", l: "chunk-hash reproducibility on re-ingest" },
              { v: "0", l: "required cloud calls to answer" },
              { v: "3", l: "retrieval signals fused per query" },
            ].map((m, i) => (
              <Reveal key={m.l} delay={i * 0.05}>
                <div className="card h-full text-center">
                  <p className="font-display text-4xl text-ember">{m.v}</p>
                  <p className="mt-2 text-sm leading-relaxed text-mute">{m.l}</p>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.1}>
            <p className="mx-auto mt-8 max-w-xl text-center text-sm text-mute">
              <Gauge size={14} className="mr-1 inline text-ember" />
              Verify, don't trust — the evaluation harness ships with the
              open-source core.
            </p>
          </Reveal>
        </div>
      </section>

      {/* SOVEREIGNTY / VISION */}
      <section id="vision" className="relative scroll-mt-32 overflow-hidden border-y border-white/5 py-28 md:py-36">
        <div
          className="absolute inset-0 opacity-20"
          style={{
            backgroundImage: `url(${DATA_IMG})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-ink via-ink/85 to-ink" />
        <div className="container-x relative">
          <Reveal>
            <p className="overline">Why we're building this</p>
            <h2 className="mt-5 max-w-4xl font-display text-4xl font-light leading-[1.1] tracking-tight text-paper md:text-6xl">
              Memory is infrastructure. It should belong to{" "}
              <span className="italic text-ember">you</span> — not to someone
              else's cloud.
            </h2>
          </Reveal>

          <div className="mt-16 grid gap-8 md:grid-cols-3">
            {[
              {
                t: "India's AI sovereignty",
                d: "The intelligence layer of the next decade shouldn't depend on foreign clouds holding the nation's context. Smritium keeps the memory substrate on your own hardware, on Indian soil — a building block for sovereign AI.",
              },
              {
                t: "Enterprise AI sovereignty",
                d: "Your institutional knowledge is your moat. It should compound inside your walls — not inside a vendor's model, meter, or terms of service you don't control.",
              },
              {
                t: "Data privacy by construction",
                d: "Privacy that is architectural, not a checkbox. Capture, embedding, retrieval and reasoning stay on-device. What never leaves cannot leak.",
              },
            ].map((x, i) => (
              <Reveal key={x.t} delay={i * 0.08}>
                <div className="h-full border-l border-ember/40 pl-6">
                  <h3 className="font-display text-2xl text-paper">{x.t}</h3>
                  <p className="mt-3 leading-relaxed text-mute">{x.d}</p>
                </div>
              </Reveal>
            ))}
          </div>

          <Reveal delay={0.12}>
            <div className="mt-16 grid items-center gap-6 md:grid-cols-[1.2fr_0.8fr]">
              <ElevateBadge variant="card" />
              <div className="text-sm leading-relaxed text-mute">
                <p>
                  Built as research, shipped as product — developed by{" "}
                  <a
                    href="https://datafrontier.co"
                    target="_blank"
                    rel="noreferrer"
                    className="text-paper underline decoration-ember/50 underline-offset-2 transition-colors hover:text-ember"
                  >
                    DataFrontier Innovations
                  </a>{" "}
                  with a provisional patent on multi-signal context rehydration,
                  published benchmarks and a reproducibility audit.
                </p>
                <p className="mt-3 font-mono text-[12px] text-mute/80">
                  smṛti · स्मृति — “that which is remembered.” Memory has been
                  treated as sacred infrastructure in Karnataka for ~3,000 years
                  longer than the cloud.
                </p>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      <Roadmap />

      {/* WAITLIST */}
      <section id="waitlist" className="scroll-mt-32 py-24 md:py-28">
        <div className="container-x">
          <div className="grid items-center gap-14 lg:grid-cols-2">
            <Reveal>
              <p className="overline">Early access</p>
              <h2 className="mt-4 font-display text-4xl font-light leading-tight tracking-tight text-paper md:text-5xl">
                Join the Smritium waitlist.
              </h2>
              <p className="mt-5 max-w-md text-lg font-light leading-relaxed text-mute">
                Be among the first to run the sovereign memory layer. Tell us
                whether you're exploring for yourself or your organisation —
                enterprise design partners get priority onboarding.
              </p>
              {count !== null && (
                <p className="mt-6 font-mono text-sm text-ember" data-testid="waitlist-count">
                  {count > 0
                    ? `${count} ${count === 1 ? "person has" : "people have"} already joined`
                    : "Be the first to reserve your place"}
                </p>
              )}
            </Reveal>
            <Reveal delay={0.1}>
              <div className="rounded-2xl border border-white/10 bg-void/70 p-7 backdrop-blur-sm">
                <WaitlistForm
                  onJoined={() => setCount((c) => (c === null ? 1 : c + 1))}
                />
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
